import { useNavigate } from "react-router-dom";
import { componentWithCondition } from "../../newLayout/page/components/hoc/componentWithConditionCheck";
import { useMemo, useState } from "react";
import { useNotification } from "../../../hooks/useNotification";
import { useConfirmDraftPurchaseOrdersMutation } from "../../../store/apiSlice/purchaseOrderApiSlice";
import { RecordPaymentModal } from "../../prepayment/components/RecordPaymentModal";
import { resolvePoOutstanding } from "../../prepayment/components/prepaymentAllocationHelpers";
import {
  closePurchaseOrderSelectionState,
  purchaseOrderStatusUpdateState,
} from "../constants/manualStatusWorkflow";
import { CreateShipmentReviewDrawer } from "../../shared/shipmentDrawer/CreateShipmentReviewDrawer";
import { createShipmentSelectionState } from "../../shared/shipmentDrawer/createShipmentFromPurchaseHelpers";
import { UpdatePurchaseOrderStatusModal } from "./UpdatePurchaseOrderStatusModal";
import { ClosePurchaseOrderModal } from "./ClosePurchaseOrderModal";
import { ButtonSpinner } from "../../../components/atoms/Button/Button.styles";
import { ListActionBurgerMenu } from "../../newLayout/page/components/OptionsForView/ListActionBurgerMenu";

/**
 * PurchaseOrderListActions Component
 *
 * Renders action buttons for performing operations on selected purchase orders:
 * - "Edit" (only for a single order)
 * - "Record Payment" (same supplier and currency)
 * - "Confirm Draft" (Draft / Draft Sent only)
 */

export const PurchaseOrderListActions = ({
  selectedOrderId,
  setSelectedOrder,
  selectedOrderFromListview,
  getConsignmentByRule,
}) => {
  const navigate = useNavigate();
  const notify = useNotification();
  const [isRecordPaymentOpen, setIsRecordPaymentOpen] = useState(false);
  const [isUpdateStatusOpen, setIsUpdateStatusOpen] = useState(false);
  const [isCloseOrderOpen, setIsCloseOrderOpen] = useState(false);
  const [isCreateShipmentOpen, setIsCreateShipmentOpen] = useState(false);
  const [isAddToShipmentOpen, setIsAddToShipmentOpen] = useState(false);
  const [confirmDraft, { isLoading: isConfirmingDraft }] = useConfirmDraftPurchaseOrdersMutation();

  const manualStatusContext = useMemo(
    () => purchaseOrderStatusUpdateState(selectedOrderFromListview),
    [selectedOrderFromListview],
  );

  const closeOrderContext = useMemo(
    () => closePurchaseOrderSelectionState(selectedOrderFromListview),
    [selectedOrderFromListview],
  );

  const confirmDraftContext = useMemo(() => {
    if (!selectedOrderFromListview?.length) {
      return { ok: false, reason: "Select at least one purchase order." };
    }
    const allDraft = selectedOrderFromListview.every(
      row => row.status === "Draft" || row.status === "Draft Sent",
    );
    if (!allDraft) {
      return {
        ok: false,
        reason: "Confirm Draft is only available when every selected order is Draft.",
      };
    }
    return { ok: true };
  }, [selectedOrderFromListview]);

  const handleConfirmDraft = async () => {
    if (!confirmDraftContext.ok || isConfirmingDraft) return;
    try {
      const res = await confirmDraft({ ids: selectedOrderId }).unwrap();
      notify({ type: "success", message: res?.detail || "Draft confirmed." });
      setSelectedOrder([]);
    } catch (error) {
      notify({
        type: "error",
        message: error?.data?.detail || "Something went wrong. Please try again.",
      });
    }
  };

  const handleUpdateOrder = () => {
    navigate(`/orders/purchase/update/${selectedOrderId[0]}`);
  };

  const handleCreateOrder = () => {
    navigate(`/orders/purchase/create`);
  };

  const createShipmentContext = useMemo(
    () => createShipmentSelectionState(selectedOrderFromListview),
    [selectedOrderFromListview],
  );

  const recordPaymentContext = useMemo(() => {
    if (selectedOrderFromListview.length === 0) {
      return { canRecord: false, reason: "Select at least one purchase order." };
    }
    const supplierIds = new Set(
      selectedOrderFromListview.map(item => item.supplier_id).filter(Boolean),
    );
    const currencyIds = new Set(
      selectedOrderFromListview.map(item => item.currency?.id).filter(Boolean),
    );
    if (supplierIds.size !== 1 || currencyIds.size !== 1) {
      return {
        canRecord: false,
        reason: "Selected purchase orders must share the same supplier and currency.",
      };
    }
    return {
      canRecord: true,
      initialValues: {
        supplier_id: [...supplierIds][0],
        currency_id: [...currencyIds][0],
        poOutstandingById: Object.fromEntries(
          selectedOrderFromListview
            .map(item => [item.id, resolvePoOutstanding(item)])
            .filter(([, outstanding]) => outstanding != null),
        ),
        allocations: selectedOrderFromListview.map(item => ({
          purchase_order_id: item.id,
          allocated_amount: "",
        })),
      },
    };
  }, [selectedOrderFromListview]);

  const burgerItems = useMemo(
    () => [
      {
        key: "update-status",
        label: "Update status",
        onClick: () => setIsUpdateStatusOpen(true),
        disabled: !manualStatusContext.ok,
        title: manualStatusContext.ok ? "" : manualStatusContext.reason,
      },
      {
        key: "close-order",
        label: "Close order",
        onClick: () => setIsCloseOrderOpen(true),
        disabled: !closeOrderContext.ok,
        title: closeOrderContext.ok ? "" : closeOrderContext.reason,
      },
      {
        key: "record-payment",
        label: "Record Payment",
        onClick: () => setIsRecordPaymentOpen(true),
        disabled: !recordPaymentContext.canRecord,
        title: recordPaymentContext.canRecord ? "" : recordPaymentContext.reason,
      },
      {
        key: "create-shipment",
        label: "Create Shipment",
        onClick: () => setIsCreateShipmentOpen(true),
        disabled: !createShipmentContext.ok,
        title: createShipmentContext.ok ? "" : createShipmentContext.reason,
      },
      {
        key: "add-to-shipment",
        label: "Add to shipment",
        onClick: () => setIsAddToShipmentOpen(true),
        disabled: !createShipmentContext.ok,
        title: createShipmentContext.ok ? "" : createShipmentContext.reason,
      },
    ],
    [
      closeOrderContext,
      createShipmentContext,
      manualStatusContext,
      recordPaymentContext,
    ],
  );

  return (
    <>
      {isCloseOrderOpen && (
        <ClosePurchaseOrderModal
          closeModal={() => setIsCloseOrderOpen(false)}
          orders={selectedOrderFromListview}
          setSelectedOrder={setSelectedOrder}
        />
      )}
      {isUpdateStatusOpen && manualStatusContext.ok && (
        <UpdatePurchaseOrderStatusModal
          closeModal={() => setIsUpdateStatusOpen(false)}
          orders={selectedOrderFromListview}
          currentStatus={manualStatusContext.currentStatus}
          allowedTargets={manualStatusContext.allowedTargets}
          cancelOnly={manualStatusContext.cancelOnly}
          setSelectedOrder={setSelectedOrder}
          onChooseClose={() => {
            setIsUpdateStatusOpen(false);
            setIsCloseOrderOpen(true);
          }}
        />
      )}
      {isRecordPaymentOpen && recordPaymentContext.canRecord && (
        <RecordPaymentModal
          isOpen={isRecordPaymentOpen}
          onClose={() => setIsRecordPaymentOpen(false)}
          onSuccess={() => getConsignmentByRule?.()}
          initialValues={recordPaymentContext.initialValues}
          lockSupplier
          lockCurrency
        />
      )}
      {isCreateShipmentOpen && createShipmentContext.ok && (
        <CreateShipmentReviewDrawer
          isOpen={isCreateShipmentOpen}
          onClose={() => setIsCreateShipmentOpen(false)}
          orders={selectedOrderFromListview}
          setSelectedOrder={setSelectedOrder}
          onSuccess={() => getConsignmentByRule?.()}
        />
      )}
      {isAddToShipmentOpen && createShipmentContext.ok && (
        <CreateShipmentReviewDrawer
          isOpen={isAddToShipmentOpen}
          mode="add"
          onClose={() => setIsAddToShipmentOpen(false)}
          orders={selectedOrderFromListview}
          setSelectedOrder={setSelectedOrder}
          onSuccess={() => getConsignmentByRule?.()}
        />
      )}
      <li className={`tableButton activeState`}>
        <button onClick={handleCreateOrder}>Create</button>
      </li>
      <li className={`tableButton ${selectedOrderId.length === 1 ? "activeState" : ""}`}>
        <button
          disabled={selectedOrderId.length !== 1}
          title={selectedOrderId.length === 1 ? "" : "Select a single purchase order to edit."}
          onClick={handleUpdateOrder}
        >
          Edit
        </button>
      </li>
      <li className={`tableButton ${confirmDraftContext.ok ? "activeState" : ""}`}>
        <button
          disabled={!confirmDraftContext.ok || isConfirmingDraft}
          title={confirmDraftContext.ok ? "" : confirmDraftContext.reason}
          onClick={handleConfirmDraft}
          style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
        >
          {isConfirmingDraft && <ButtonSpinner aria-hidden="true" />}
          {isConfirmingDraft ? "Confirming…" : "Confirm Draft"}
        </button>
      </li>
      <ListActionBurgerMenu items={burgerItems} />
    </>
  );
};

export const PurchaseOrderListActionsWithCheck = componentWithCondition(PurchaseOrderListActions);
